import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { supabase } from "@/lib/supabase";
import { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

function mapUser(sUser) {
  if (!sUser) return false;
  return {
    id: sUser.id,
    email: sUser.email,
    name: sUser.user_metadata?.name || sUser.email,
    role: sUser.user_metadata?.role || "user",
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [completed, setCompleted] = useState(new Set());
  const [progressLoaded, setProgressLoaded] = useState(false);
  const [stats, setStats] = useState({ workouts: 0, exercises: 0 });
  const userIdRef = useRef(null);
  const registeringRef = useRef(false);

  const loadProgress = useCallback(async (uid) => {
    try {
      const { data, error } = await supabase
        .from("user_progress")
        .select("completed, stats")
        .eq("user_id", uid)
        .maybeSingle();
      if (error) throw error;
      setCompleted(new Set(data?.completed || []));
      const loadedStats = data?.stats ?? { workouts: 0, exercises: 0 };
      setStats(loadedStats);
    } catch (e) {
      setCompleted(new Set());
    } finally {
      setProgressLoaded(true);
    }
  }, []);

  const persist = useCallback(async (nextSet) => {
    const uid = userIdRef.current;
    if (!uid) return;
    try {
      await supabase.from("user_progress").upsert({
        user_id: uid,
        completed: Array.from(nextSet),
        updated_at: new Date().toISOString(),
      });
    } catch (e) {
      /* manteniamo lo stato ottimistico anche se la rete fallisce */
    }
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const sUser = session?.user || null;
      userIdRef.current = sUser?.id || null;
      if (sUser) {
        setUser(mapUser(sUser));
        loadProgress(sUser.id);
      } else {
        setUser(false);
      }
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      const sUser = session?.user || null;
      userIdRef.current = sUser?.id || null;
      if (sUser) {
        if (registeringRef.current) return;
        setUser(mapUser(sUser));
      } else {
        setUser(false);
        setCompleted(new Set());
        setProgressLoaded(false);
      }
    });

    return () => sub.subscription.unsubscribe();
  }, [loadProgress]);

  const login = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    userIdRef.current = data.user.id;
    setUser(mapUser(data.user));
    await loadProgress(data.user.id);
  };

  const register = async (name, email, password, inviteCode) => {
    registeringRef.current = true;
    let sUser = null;
    try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });

    if (error) {
      if (error.message?.toLowerCase().includes("already registered")) {
        const { data: loginData, error: loginError } = await supabase.auth.signInWithPassword({ email, password });
        if (loginError) throw new Error("Email già registrata");
        sUser = loginData.user;
      } else {
        throw error;
      }
    } else {
      sUser = data.user;
    }

    if (sUser) {
      userIdRef.current = sUser.id;
      const { error: rpcError } = await supabase.rpc("redeem_invite_code", { invite: inviteCode });
      if (rpcError) {
        await supabase.auth.signOut();
        userIdRef.current = null;
        setUser(false);
        setCompleted(new Set());
        setProgressLoaded(false);
        throw new Error("Codice di invito non valido o già utilizzato");
      }
      setUser(mapUser(sUser));
      await loadProgress(sUser.id);
    }
    } finally {
      registeringRef.current = false;
    }
  };

  const bumpStats = async (w, e) => {
    setStats((prev) => ({ workouts: prev.workouts + w, exercises: prev.exercises + e }));
    await supabase.rpc("increment_stats", { w, e });
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      /* ignore */
    }
    userIdRef.current = null;
    setUser(false);
    setCompleted(new Set());
    setProgressLoaded(false);
    setStats({ workouts: 0, exercises: 0 });
  };

  const toggleItem = async (id, done) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      if (done) next.add(id);
      else next.delete(id);
      return next;
    });
    await supabase.rpc(done ? "add_completed_item" : "remove_completed_item", { item: id });
  };

  const setManyItems = async (ids, done) => {
    setCompleted((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => (done ? next.add(id) : next.delete(id)));
      return next;
    });
    await Promise.all(
      ids.map((id) =>
        supabase.rpc(done ? "add_completed_item" : "remove_completed_item", { item: id })
      )
    );
  };

  const resetProgress = async () => {
    const next = new Set();
    setCompleted(next);
    await persist(next);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        completed,
        progressLoaded,
        stats,
        login,
        register,
        logout,
        toggleItem,
        setManyItems,
        resetProgress,
        bumpStats,
        formatApiError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);